import { z } from 'zod';

/**
 * Reporting contract (CLAUDE.md §10 `/api/reports`, §15). Single source of truth for the
 * report-run payloads — imported by the Worker `reports` routes, their tests, and the
 * `/iso50001` + `/reports` screens. Reports are rendered server-side to PDF, stored in R2
 * (§7.2 `reports/{site}/{type}/{period}.pdf`), and listed from `report_runs`.
 *
 * M08 implements the ISO 50001 management-review pack; the remaining types (monthly energy,
 * M&V verification, board ESG) are scaffolded here and completed in M09. Running a report is
 * gated on `reports:run`; listing/downloading on `reports:read`. Every run writes an audit row.
 */

export const ReportTypeSchema = z.enum([
  'iso50001_review',
  'monthly_energy',
  'mv_verification',
  'esg_summary',
]);
export type ReportType = z.infer<typeof ReportTypeSchema>;

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  iso50001_review: 'ISO 50001 management-review pack',
  monthly_energy: 'Monthly energy report',
  mv_verification: 'M&V verification report',
  esg_summary: 'Board ESG summary',
};

// ── Read row ──────────────────────────────────────────────────────────────────
export const ReportRunRowSchema = z.object({
  id: z.string(),
  type: ReportTypeSchema,
  /** Reporting period, YYYY-MM (or YYYY for annual packs). */
  period: z.string(),
  /** R2 object key of the generated PDF, or null if generation has not completed. */
  r2Key: z.string().nullable(),
  /** Generation timestamp, epoch ms. */
  generatedAt: z.number(),
  generatedBy: z.string().nullable(),
});
export type ReportRunRow = z.infer<typeof ReportRunRowSchema>;

export const ReportListResponseSchema = z.object({ reports: z.array(ReportRunRowSchema) });
export type ReportListResponse = z.infer<typeof ReportListResponseSchema>;

// ── Run input ────────────────────────────────────────────────────────────────
export const ReportRunInputSchema = z.object({
  siteId: z.string().min(1),
  type: ReportTypeSchema,
  /** YYYY-MM or YYYY. Defaults to the current period server-side when omitted. */
  period: z.string().min(1).optional(),
});
export type ReportRunInput = z.infer<typeof ReportRunInputSchema>;

export const ReportRunResponseSchema = z.object({ report: ReportRunRowSchema });
export type ReportRunResponse = z.infer<typeof ReportRunResponseSchema>;
